/**
 * API Service with unified authentication support
 * 
 * Handles API requests with automatic authentication header injection.
 * Supports both Cognito (production) and local mock authentication (development).
 * 
 * Requirements: 18.6, 18.11
 */

import axios, { AxiosInstance } from 'axios';
import { Auth } from '@aws-amplify/auth';
import { awsConfig } from '../config/aws';

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: awsConfig.API.endpoint,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth headers
    this.client.interceptors.request.use(
      async (config) => {
        // Get Cognito JWT token from Amplify session
        try {
          const session = await Auth.currentSession();
          const idToken = session.getIdToken().getJwtToken();
          if (idToken) {
            config.headers.Authorization = `Bearer ${idToken}`;
          }
        } catch (error) {
          console.error('Failed to get auth token:', error);
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        // Extract error message from response
        if (error.response?.data?.error) {
          const backendError = error.response.data.error;
          if (typeof backendError === 'string') {
            // Simple error format: { error: "message" }
            error.message = backendError;
          } else {
            // Structured error format: { error: { code, message }, requestId, timestamp }
            error.message = backendError.message || error.message;
            error.code = backendError.code;
          }
        }
        
        if (error.response?.status === 401) {
          // Sign out from Cognito
          try {
            await Auth.signOut();
          } catch (e) {
            console.error('Sign out error:', e);
          }
          
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, params?: any): Promise<T> {
    const response = await this.client.get<T>(url, { params });
    return response.data;
  }

  async post<T>(url: string, data?: any): Promise<T> {
    const response = await this.client.post<T>(url, data);
    return response.data;
  }

  async put<T>(url: string, data?: any): Promise<T> {
    const response = await this.client.put<T>(url, data);
    return response.data;
  }

  async delete<T>(url: string): Promise<T> {
    const response = await this.client.delete<T>(url);
    return response.data;
  }

  async upload(url: string, file: File, onProgress?: (progress: number) => void): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.client.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });

    return response.data;
  }
}

export const apiService = new ApiService();
